import { type ISurveyCreator, SurveyQuestion } from "survey-react-ui";
import type { Question } from "survey-core";
import { useSurveyQuestionRerender } from "../questions/useSurveyQuestionRerender";

/**
 * The per-question chrome SurveyJS wraps around every question control:
 * label, required marker, description and validation errors. Registered as
 * the `question` element factory (overriding SurveyJS's default
 * `SurveyQuestion` markup) so every question type gets the same admin-v2
 * treatment regardless of which renderer draws its control.
 *
 * The existing `Field` primitive is deliberately not reused: it is coupled
 * to TanStack Form (`field: AnyFieldApi`, its own blur/submission error
 * timing), while SurveyJS owns value, touched and error state on the
 * question model here. The visual contract is copied instead: same label
 * styling, same `*`/`(optioneel)` markers, same error/description text.
 *
 * The subscription is what makes validation visible: errors set on blur or
 * on `survey.tryComplete()` land on the model, and without it this chrome
 * would keep showing the pre-validation DOM.
 */
export function SurveyQuestionChrome({
  question,
  creator,
}: {
  readonly question: Question;
  readonly creator: ISurveyCreator;
}) {
  useSurveyQuestionRerender(question);

  const body = SurveyQuestion.renderQuestionBody(creator, question);
  const description = question.hasDescription
    ? question.locDescription.renderedHtml
    : undefined;
  const errors = question.errors;

  const title = (
    <>
      {question.fullTitle}
      {question.isRequired ? (
        <span className="text-destructive" aria-hidden="true">
          {" "}
          *
        </span>
      ) : (
        <span className="ml-1 text-xs font-normal text-muted-foreground">
          (optioneel)
        </span>
      )}
    </>
  );
  const descriptionEl = description ? (
    <p
      id={question.ariaDescriptionId}
      className="text-xs text-muted-foreground"
    >
      {description}
    </p>
  ) : null;
  const errorsEl =
    errors.length > 0 ? (
      <div role="alert" id={`${question.id}_errors`}>
        {errors.map((error) => (
          <p key={error.getText()} className="text-xs text-destructive">
            {error.getText()}
          </p>
        ))}
      </div>
    ) : null;

  // The question types that render a multi-choice group rather than a single
  // labelled control — in admin-v2 only the checkbox renderer does this.
  if (question.getType() === "checkbox") {
    // No single labelled control exists for a checkbox group: the question is
    // a fieldset whose legend carries the label, and every checkbox is
    // labelled by its own choice text. Group errors are announced at the
    // group's `aria-describedby` target.
    return (
      <fieldset
        className="flex flex-col gap-1.5 border-0 py-3"
        aria-invalid={question.a11y_input_ariaInvalid}
        aria-describedby={question.a11y_input_ariaDescribedBy ?? undefined}
      >
        <legend
          id={question.ariaTitleId}
          className="text-sm font-medium text-foreground"
        >
          {title}
        </legend>
        {descriptionEl}
        {body}
        {errorsEl}
      </fieldset>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 py-3">
      <label
        id={question.ariaTitleId}
        htmlFor={question.inputId}
        className="text-sm font-medium text-foreground"
      >
        {title}
      </label>

      {descriptionEl}

      {body}

      {errorsEl}
    </div>
  );
}
